import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { TokenService } from './auth/token.service';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})

/**
 * Servicio que permite manejar de manera dinamica el id para Google Analytics de acuerdo a un ambiente.
 *
 * @author Alex Fernando Caldas Garcia <acaldas@heinsohn.com.co>
 */
export class GoogleAnalyticsService {

  private tokenService = inject(TokenService);

  init(): void {
    const gaId = environment.googleAnalyticsId;

    // Google Analytics ID no configurado → no se hace absolutamente nada
    if (!gaId || gaId.trim() === '') {
      return;
    }

    // Evita cargar GA más de una vez
    if (document.getElementById('ga-script')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', gaId);
  }

  /**
   * Establece las propiedades de usuario en Google Analytics para tener un mayor control
   * de quién accede a la aplicación.
   *
   * @param userProperties - Objeto con las propiedades del usuario
   */
  setUserProperties(userProperties: {
    username?: string;
    id_entidad?: string;
    nombre_entidad?: string;
    id_rol?: number;
    nombre_rol?: string;
  }): void {
    // Verificar que gtag esté disponible
    if (typeof window.gtag === 'undefined') {
      console.warn('Google Analytics no está inicializado. No se pueden establecer propiedades de usuario.');
      return;
    }

    // Filtrar propiedades undefined/null para no enviar valores vacíos
    const filteredProperties: Record<string, any> = {};
    
    if (userProperties.username) {
      filteredProperties['username'] = userProperties.username;
    }
    if (userProperties.id_entidad) {
      filteredProperties['id_entidad'] = userProperties.id_entidad;
    }
    if (userProperties.nombre_entidad) {
      filteredProperties['nombre_entidad'] = userProperties.nombre_entidad;
    }
    if (userProperties.id_rol) {
      filteredProperties['id_rol'] = userProperties.id_rol;
    }
    if (userProperties.nombre_rol) {
      filteredProperties['nombre_rol'] = userProperties.nombre_rol;
    }

    // Solo establecer propiedades si hay al menos una propiedad válida
    if (Object.keys(filteredProperties).length > 0) {
      window.gtag('set', 'user_properties', filteredProperties);
    }
  }

  /**
   * Establece automáticamente las propiedades de usuario en Google Analytics
   * obteniendo los datos del usuario autenticado desde TokenService.
   * Este método debe ser llamado después de que el usuario se autentique.
   */
  setUserPropertiesFromSession(): void {
    const dataUser = this.tokenService.getDataUser();
    
    // Solo establecer propiedades si el usuario está autenticado
    if (!dataUser) {
      return;
    }

    // Obtener el primer rol activo (si existe)
    const roles = dataUser.roles;
    const firstRole = roles && roles.length > 0 ? roles[0] : null;
    const userData = firstRole?.user;

    // Preparar las propiedades de usuario
    const userProperties = {
      username: userData?.username,
      id_entidad: userData?.entidadCodigo,
      nombre_entidad: userData?.entidadNombre,
      id_rol: userData?.idRol,
      nombre_rol: userData?.nombreRol
    };

    // Establecer las propiedades
    this.setUserProperties(userProperties);
  }
}
